import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { BannerElement, EditorState, BannerSize, DataFeed } from '../types';

export const useEditor = (
  initialSize: BannerSize,
  initialDataFeed: DataFeed | null = null,
  initialDataFeedArray: DataFeed[] = []
) => {
  const [state, setState] = useState<EditorState>({
    selectedElement: null,
    currentSize: initialSize,
    elements: initialSize.elements || [],
    zoom: 1,
    showGrid: true,
    snapToGrid: true,
    dataFeed: initialDataFeed,
    dataFeedArray: initialDataFeedArray,
    templates: [],
    timeline: {
      duration: 5,
      isPlaying: false,
      currentTime: 0
    }
  });

  const addElement = useCallback((type: BannerElement['type'], x = 50, y = 50) => {
    const newElement: BannerElement = {
      id: uuidv4(),
      type,
      x,
      y,
      width: type === 'text' ? 200 : type === 'button' ? 150 : type === 'divider' ? 200 : 100,
      height: type === 'text' ? 40 : type === 'button' ? 40 : type === 'divider' ? 2 : 100,
      zIndex: state.elements.length + 1,
      content: type === 'text' ? 'Sample Text' : type === 'button' ? 'Click Here' : undefined,
      imageUrl: type === 'image' ? '' : undefined,
      dataBinding: undefined,
      styles: {
        fontSize: type === 'text' ? 16 : type === 'button' ? 14 : undefined,
        fontWeight: type === 'text' ? '400' : type === 'button' ? '500' : undefined,
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        color: type === 'text' || type === 'button' ? '#000000' : undefined,
        backgroundColor: type === 'shape' ? '#176DB7' : type === 'button' ? '#176DB7' : type === 'divider' ? '#000000' : 'transparent',
        borderRadius: type === 'button' ? 4 : 0,
        padding: type === 'text' || type === 'button' ? 8 : 0,
        opacity: 1
      },
      animation: {
        type: 'none',
        duration: 1,
        delay: 0,
        easing: 'ease-in-out'
      }
    };

    setState(prev => ({
      ...prev,
      elements: [...prev.elements, newElement],
      selectedElement: newElement.id
    }));
  }, [state.elements.length]);

  const selectElement = useCallback((id: string | null) => {
    setState(prev => ({ ...prev, selectedElement: id }));
  }, []);

  const updateElement = useCallback((id: string, updates: Partial<BannerElement>) => {
    setState(prev => ({
      ...prev,
      elements: prev.elements.map(el => 
        el.id === id ? { ...el, ...updates } : el
      )
    }));
  }, []);

  const deleteElement = useCallback((id: string) => {
    setState(prev => ({
      ...prev,
      elements: prev.elements.filter(el => el.id !== id),
      selectedElement: prev.selectedElement === id ? null : prev.selectedElement
    }));
  }, []);

  const moveElementLayer = useCallback((id: string, direction: 'up' | 'down') => {
    setState(prev => {
      const elements = [...prev.elements];
      const elementIndex = elements.findIndex(el => el.id === id);
      
      if (elementIndex === -1) return prev;
      
      if (direction === 'up' && elementIndex < elements.length - 1) {
        [elements[elementIndex], elements[elementIndex + 1]] = [elements[elementIndex + 1], elements[elementIndex]];
      } else if (direction === 'down' && elementIndex > 0) {
        [elements[elementIndex], elements[elementIndex - 1]] = [elements[elementIndex - 1], elements[elementIndex]];
      }
      
      // Update z-indexes
      elements.forEach((el, index) => {
        el.zIndex = index + 1;
      });
      
      return { ...prev, elements };
    });
  }, []);

  const setZoom = useCallback((zoom: number) => {
    setState(prev => ({ ...prev, zoom }));
  }, []);

  const toggleGrid = useCallback(() => {
    setState(prev => ({ ...prev, showGrid: !prev.showGrid }));
  }, []);

  const setDataFeed = useCallback((dataFeed: DataFeed | DataFeed[]) => {
    if (Array.isArray(dataFeed)) {
      setState(prev => ({ 
        ...prev, 
        dataFeed: dataFeed[0] || null,
        dataFeedArray: dataFeed
      }));
    } else {
      setState(prev => ({ 
        ...prev, 
        dataFeed,
        dataFeedArray: [dataFeed]
      }));
    }
  }, []);

  const selectedElement = state.elements.find(el => el.id === state.selectedElement) || null;

  return {
    state,
    selectedElement,
    addElement,
    selectElement,
    updateElement,
    deleteElement,
    moveElementLayer,
    setZoom,
    toggleGrid,
    setDataFeed
  };
};